// Magic-link interstitial for tenant sign-in (DESIGN §4.2). The email link
// from `/auth/request` (or signup, or onboarding invite) lands here, NOT
// directly at the Worker's POST /auth/verify. A human click redeems the
// single-use token; passive URL prefetchers can only GET this page and cannot
// consume the token on the user's behalf. See [`AdminVerify`](./AdminVerify.tsx)
// for the full prefetch-defence rationale (same mitigation, separate copy
// because tenant users hit a different mid-funnel than operators).
//
// Slice 37 — admin-provisioned onboarding. When a BesideU Admin provisions an
// org, the invited HR Admin's first `/auth/verify` returns `onboardingRequired`
// (no session yet) instead of a landing path: the org was created with NULL
// DPA / safety-contact fields the operator can't accept on the tenant's behalf.
// We render the DPA acceptance + safety-contact acknowledgement step inline and
// POST `/auth/onboarding/complete`, which records the acceptances, activates the
// org, and mints the session. The onboarding token is persisted to
// `sessionStorage` (NOT the URL) so a reload mid-onboarding resumes rather than
// stranding the HR Admin on a consumed magic link.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  HEADCOUNT_BANDS,
  type HeadcountBand,
  type SignupDpaResponse,
} from '@besideu/shared/signup';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput, Select } from '@/components/ui/Field.tsx';
import { DpaAndSafetyAcceptance } from '@/components/DpaAndSafetyAcceptance.tsx';

// sessionStorage key for the in-flight onboarding token. Kept out of the URL
// (same posture as the magic-link token) but persisted across reloads so the
// HR Admin can refresh / read the DPA at length without losing their place.
const ONBOARDING_TOKEN_KEY = 'besideu.hrOnboardingToken';

type VerifyResponse =
  | { landingPath: string }
  | { onboardingRequired: true; onboardingToken: string };

export function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Capture once at mount, then strip from the URL bar — see AdminVerify for rationale.
  const [token] = useState(() => params.get('token') ?? '');
  // Resume a persisted onboarding token ONLY when there's no fresh magic-link
  // token in the URL — a new link click must start the verify flow fresh.
  // Deliberate trade-off: persisting across reloads is what stops a refresh
  // mid-onboarding from stranding the HR Admin on a consumed magic link. The
  // cost is that an abandoned-but-unexpired flow can resurface on a later
  // token-less visit to /verify; that self-heals (the server token's ~15-min
  // TTL expires and the first submit flips to the terminal expired state, which
  // clears it). Accepted over the worse alternative of stranding a refresh.
  const [onboardingToken, setOnboardingToken] = useState<string | null>(() => {
    if (params.get('token')) return null;
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(ONBOARDING_TOKEN_KEY);
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-submit on mount when there's a fresh token in the URL. The SPA
  // interstitial (DESIGN §4.2) is the prefetch-defence: a passive GET renders
  // the page, but only an explicit POST /auth/verify consumes the token.
  // Auto-firing that POST on mount is safe under the same threat model —
  // prefetchers / link-rewriters don't execute React effects — and it
  // eliminates the "user clicks email link, lands on page, doesn't realise
  // there's a Continue button" failure mode that surfaced in the staging
  // demo. The button stays as a fallback for the rare auto-submit failure.
  // Intentionally depends on nothing (mount-only) — re-firing on token
  // change would double-consume if React re-renders with a new URL param.
  useEffect(() => {
    if (token && !onboardingToken) void onContinue();
  }, []);

  function persistOnboardingToken(next: string) {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(ONBOARDING_TOKEN_KEY, next);
    setOnboardingToken(next);
  }

  async function onContinue() {
    setError(null);
    setPending(true);
    try {
      // No `deviceType` hint any more (PO 2026-07-31). It existed so the
      // verify handler could return a role × device landing path; every
      // tenant role now lands on one responsive route tree that picks its
      // layout by viewport, so the server has no device decision to make and
      // the whole UA-vs-SPA-hint trust model was removed with it.
      const res = await api.post<VerifyResponse>('/auth/verify', { token });
      if ('onboardingRequired' in res) {
        persistOnboardingToken(res.onboardingToken);
        return;
      }
      // Session cookie is set by the response; React Router loaders on the
      // destination route will see it on the next request.
      await navigate(res.landingPath);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('This link has expired or has already been used. Request a new sign-in link.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('This link is malformed. Request a new sign-in link.');
      } else {
        setError('Something went wrong. Try again, or request a new link.');
      }
    } finally {
      setPending(false);
    }
  }

  // Onboarding takes precedence: a persisted token means a refresh/back-nav
  // mid-onboarding lands here even though the URL token is gone.
  if (onboardingToken) {
    return <HrOnboardingStep onboardingToken={onboardingToken} />;
  }

  if (!token) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <h1 className="text-2xl font-semibold tracking-tight">Sign-in</h1>
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          No token in URL — open the link from your sign-in email.
        </p>
        <p className="mt-4 text-sm">
          <Link className="underline underline-offset-2 hover:no-underline" to="/sign-in">
            Back to sign-in
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">Continue to besideU</h1>
      <p className="mt-2 text-sm text-muted-foreground">Click Continue to finish signing in.</p>
      <div className="mt-6">
        <Button onClick={onContinue} loading={pending}>
          Continue
        </Button>
      </div>
      {error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          {error}{' '}
          <Link className="underline underline-offset-2 hover:no-underline" to="/sign-in">
            Request a new link
          </Link>
          .
        </p>
      ) : null}
      <p className="mt-6 text-xs text-muted-foreground">
        Single-use link. If your email provider previewed it before you got here, you may need to
        request a new one.
      </p>
    </main>
  );
}

// Gated first-sign-in step for an HR Admin whose org was provisioned by a
// BesideU Admin. Accept the DPA + acknowledge safety-contact, then the org
// activates and the session is minted (mirrors the self-serve signup form's two
// acceptances, just moved post-magic-link). The active DPA wording is fetched
// live so the version always matches what the server is enforcing.
function HrOnboardingStep({ onboardingToken }: { onboardingToken: string }) {
  const navigate = useNavigate();
  const [dpa, setDpa] = useState<SignupDpaResponse | null>(null);
  const [dpaLoadError, setDpaLoadError] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [safetyAck, setSafetyAck] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Terminal state: the onboarding token is dead (expired/consumed). We stop
  // showing the form so the HR Admin can't re-POST a token that will only 401
  // again — the recoverable action is an admin resend, not a retry.
  const [tokenExpired, setTokenExpired] = useState(false);
  // PROJECT §4 Step 1 — headcount + primary/escalation contacts. Required for
  // the admin-provisioned path so the org row is congruent with the self-serve
  // signup path before activation.
  const [headcountBand, setHeadcountBand] = useState<HeadcountBand | ''>('');
  const [primaryName, setPrimaryName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [escalationName, setEscalationName] = useState('');
  const [escalationEmail, setEscalationEmail] = useState('');

  const loadDpa = useCallback(async (): Promise<void> => {
    setDpaLoadError(false);
    try {
      setDpa(await api.get<SignupDpaResponse>('/api/organizations/dpa'));
    } catch {
      setDpaLoadError(true);
    }
  }, []);

  useEffect(() => {
    // Guard against a setState after unmount if the user navigates away while
    // the initial DPA fetch is still in flight.
    let mounted = true;
    void (async () => {
      try {
        const res = await api.get<SignupDpaResponse>('/api/organizations/dpa');
        if (mounted) setDpa(res);
      } catch {
        if (mounted) setDpaLoadError(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  function clearPersistedToken() {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
  }

  async function onComplete() {
    // Defense-in-depth: the Button is disabled until both boxes are ticked, but
    // re-check here too — in this (non-<form>) context the checkboxes' `required`
    // attr is inert, so this is the last guard on the consent submit.
    if (!dpa || !dpaAccepted || !safetyAck) return;
    if (
      !headcountBand ||
      primaryName.trim() === '' ||
      primaryEmail.trim() === '' ||
      escalationName.trim() === '' ||
      escalationEmail.trim() === ''
    ) {
      setError('Please choose a headcount band and complete both contact pairs.');
      return;
    }
    setError(null);
    setPending(true);
    try {
      const res = await api.post<{ landingPath: string }>('/auth/onboarding/complete', {
        onboardingToken,
        dpaVersionAccepted: dpa.version,
        hrSafetyContactAcknowledged: true,
        headcountBand,
        primaryContact: { name: primaryName.trim(), email: primaryEmail.trim() },
        escalationContact: { name: escalationName.trim(), email: escalationEmail.trim() },
      });
      clearPersistedToken();
      await navigate(res.landingPath);
    } catch (err) {
      if (err instanceof ApiError && err.message === 'dpa_version_mismatch') {
        // The DPA was updated between our fetch and this submit. Re-fetch the
        // new wording in place and make them re-accept — NO page reload (a
        // reload would lose the onboarding token and strand them).
        await loadDpa();
        setDpaAccepted(false);
        setError('The agreement was updated. Please review the new version and accept it again.');
      } else if (err instanceof ApiError && err.status === 401) {
        // Token expired/consumed — clear it so a reload doesn't retry a dead
        // token, and flip to the terminal state (form removed).
        clearPersistedToken();
        setTokenExpired(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center px-6 py-12">
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">
        Finish setting up your organisation
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Your besideU administrator has set up your organisation. Accept the agreement, share a few
        onboarding details, and you’re in.
      </p>

      {tokenExpired ? (
        <p className="mt-6 text-sm text-traffic-red" role="status">
          This onboarding link has expired. Ask your besideU administrator to resend the invite.
        </p>
      ) : dpaLoadError ? (
        <p className="mt-6 text-sm text-traffic-red" role="status">
          Could not load the Data Processing Agreement.{' '}
          <button type="button" className="underline" onClick={() => void loadDpa()}>
            Retry
          </button>
        </p>
      ) : !dpa ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading agreement…</p>
      ) : (
        <div className="mt-6 grid gap-5">
          <DpaAndSafetyAcceptance
            dpaSource={dpa.source}
            dpaVersion={dpa.version}
            dpaBody={dpa.body}
            dpaAccepted={dpaAccepted}
            onDpaAcceptedChange={setDpaAccepted}
            safetyAck={safetyAck}
            onSafetyAckChange={setSafetyAck}
          />

          {/* PROJECT §4 Step 1 — headcount + primary/escalation contacts for
              admin-provisioned onboarding. Same fields the self-serve signup
              form captures, so the org row is congruent post-activation. */}
          <Field label="Organisation size">
            {(id, desc) => (
              <Select
                id={id}
                {...desc}
                required
                value={headcountBand}
                onChange={(e) => setHeadcountBand(e.target.value as HeadcountBand | '')}
              >
                <option value="" disabled>
                  Choose a band…
                </option>
                {HEADCOUNT_BANDS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <fieldset className="grid gap-3 rounded-lg border border-border p-4">
            <legend className="px-1 text-sm font-medium text-foreground">Primary contact</legend>
            <Field label="Name">
              {(id, desc) => (
                <TextInput
                  id={id}
                  {...desc}
                  required
                  autoComplete="name"
                  value={primaryName}
                  onChange={(e) => setPrimaryName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Work email">
              {(id, desc) => (
                <TextInput
                  id={id}
                  {...desc}
                  type="email"
                  required
                  autoComplete="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                />
              )}
            </Field>
          </fieldset>
          <fieldset className="grid gap-3 rounded-lg border border-border p-4">
            <legend className="px-1 text-sm font-medium text-foreground">Escalation contact</legend>
            <Field label="Name">
              {(id, desc) => (
                <TextInput
                  id={id}
                  {...desc}
                  required
                  autoComplete="name"
                  value={escalationName}
                  onChange={(e) => setEscalationName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Work email">
              {(id, desc) => (
                <TextInput
                  id={id}
                  {...desc}
                  type="email"
                  required
                  autoComplete="email"
                  value={escalationEmail}
                  onChange={(e) => setEscalationEmail(e.target.value)}
                />
              )}
            </Field>
          </fieldset>

          {error ? (
            <p className="text-sm text-traffic-red" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            onClick={onComplete}
            loading={pending}
            disabled={
              !dpaAccepted ||
              !safetyAck ||
              !headcountBand ||
              primaryName.trim() === '' ||
              primaryEmail.trim() === '' ||
              escalationName.trim() === '' ||
              escalationEmail.trim() === ''
            }
          >
            Complete setup &amp; sign in
          </Button>
        </div>
      )}
    </main>
  );
}
